/*
  ЦИФРОВОЙ ДИАК ЗФ — PROJECT CONTEXT
  Данные с сервера (/api/workspace), сохранение с debounce для editor/admin.
*/

import React, { createContext, useContext, useState, useCallback, useEffect, useMemo, useRef } from 'react';
import {
  type EditableProject,
  type RoadmapPhase,
  type AgileRisk,
  type KPI,
  type Stakeholder,
  type BacklogItem,
  type EditableGanttTask,
  type KanbanColumn,
  generateId,
  computeProjectProgress,
  defaultKanbanColumns,
  normalizeEditableProject,
  normalizeGanttItem,
  normalizeRoadmapPhases,
  roadmapPhasesSorted,
  phaseLabelFromMeta,
  asScopeLine,
  compareGanttTasksDisplayOrder,
  syncGanttTaskPlanStatusWithToday,
} from '@/store/projectStore';
import { type GanttItem } from '@/data/projects';
import { buildReviewSnapshotRecord, type ReviewSnapshot } from '@/store/reviewSnapshotStore';
import {
  apiJson,
  moveBacklogCard,
  ApiHttpError,
  apiCreateCard,
  apiPatchCard,
  apiDeleteCard,
  apiMoveCard,
} from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { mergeProjects, mergeArrayById } from '@/lib/workspaceMerge';

/** Ответ `/api/workspace` (после бэкенда — с `docRevisions` для защиты от гонки PUT). */
type WorkspaceApiPayload = {
  projects: EditableProject[];
  ganttItems: GanttItem[];
  reviewSnapshots: ReviewSnapshot[];
  roadmapPhases?: unknown;
  docRevisions?: {
    projects?: string | null;
    ganttItems?: string | null;
    roadmapPhases?: string | null;
  };
};

const PHASE_COLORS_ROT = ['#00A651', '#0055A5', '#4A90D9', '#9333EA', '#E11D48'];

/**
 * Источник правды по статусу проекта — паспорт (`EditableProject.status`).
 * Полосы на общем ганте (`GanttItem.status`) должны это отражать —
 * иначе при смене статуса в карточке проекта цвет колбаски не меняется.
 *
 * Возвращает новый массив только если что-то изменилось (чтобы лишний раз
 * не дёргать debounce-сохранение).
 */
function syncGanttItemsStatusFromProjects(
  items: GanttItem[],
  projects: EditableProject[],
): GanttItem[] {
  if (items.length === 0) return items;
  const byProjectId = new Map(projects.map((p) => [p.id, p.status]));
  let changed = false;
  const next = items.map((g) => {
    const projectStatus = byProjectId.get(g.projectId);
    if (projectStatus === undefined) return g;
    if (g.status === projectStatus) return g;
    changed = true;
    return { ...g, status: projectStatus };
  });
  return changed ? next : items;
}

function emptyCoreProject(phaseId: string, phases: RoadmapPhase[]): EditableProject {
  const meta = phases.find((x) => x.id === phaseId);
  const now = new Date().toISOString();
  return {
    id: generateId('proj'),
    title: 'Новый проект',
    shortTitle: 'Новый',
    icon: 'FileText',
    status: 'planned',
    aiReadiness: 'needs_analysis',
    aiPotential: '',
    group: 'core',
    phaseId,
    phaseLabel: meta ? phaseLabelFromMeta(meta) : '',
    description: '',
    goal: '',
    scope: [],
    risks: [],
    kpis: [],
    stakeholders: [],
    tags: [],
    repoUrl: '',
    startDate: '',
    targetDate: '',
    progress: 0,
    ganttTasks: [],
    backlog: [],
    kanbanColumns: defaultKanbanColumns(),
    documents: [],
    orderInPhase: 0,
    updatedAt: now,
  };
}

interface ProjectContextValue {
  projects: EditableProject[];
  ganttItems: GanttItem[];
  roadmapPhases: RoadmapPhase[];
  workspaceLoading: boolean;
  canEdit: boolean;
  /** Просмотрщику (viewer) документы скрыты везде. */
  canSeeDocuments: boolean;

  updateProject: (id: string, patch: Partial<EditableProject>) => void;
  reorderCoreProjectsInPhase: (phaseId: string, orderedIds: string[]) => void;
  /** Порядок строк общего ганта внутри этапа (по id полос). */
  reorderGanttItemsForPhase: (phaseId: string, orderedGanttIds: string[]) => void;

  addRoadmapPhase: () => void;
  updateRoadmapPhase: (id: string, patch: Partial<Omit<RoadmapPhase, 'id'>>) => void;
  removeRoadmapPhase: (id: string) => void;
  addCoreProject: (phaseId: string) => void;
  removeProject: (id: string) => void;

  addScopeItem: (projectId: string, text: string) => void;
  updateScopeItem: (projectId: string, idx: number, text: string) => void;
  removeScopeItem: (projectId: string, idx: number) => void;
  reorderScopeItems: (projectId: string, oldIndex: number, newIndex: number) => void;
  toggleScopeItemDone: (projectId: string, idx: number) => void;

  addRisk: (projectId: string, risk: Omit<AgileRisk, 'id' | 'createdAt' | 'updatedAt'>) => void;
  updateRisk: (projectId: string, riskId: string, patch: Partial<AgileRisk>) => void;
  removeRisk: (projectId: string, riskId: string) => void;

  addKPI: (projectId: string, kpi: Omit<KPI, 'id'>) => void;
  updateKPI: (projectId: string, kpiId: string, patch: Partial<KPI>) => void;
  removeKPI: (projectId: string, kpiId: string) => void;

  addStakeholder: (projectId: string, sh: Omit<Stakeholder, 'id'>) => void;
  updateStakeholder: (projectId: string, shId: string, patch: Partial<Stakeholder>) => void;
  removeStakeholder: (projectId: string, shId: string) => void;

  addGanttTask: (projectId: string, task: Omit<EditableGanttTask, 'id'>) => void;
  updateGanttTask: (projectId: string, taskId: string, patch: Partial<EditableGanttTask>) => void;
  removeGanttTask: (projectId: string, taskId: string) => void;
  /** Переставить строку план-графика после drag-and-drop (позиция относительно задачи `overTaskId`). */
  reorderGanttTasksAfterDrag: (projectId: string, activeTaskId: string, overTaskId: string) => void;

  addBacklogItem: (
    projectId: string,
    item: Omit<BacklogItem, 'id' | 'createdAt' | 'updatedAt' | 'kanbanOrder'> & {
      kanbanOrder?: number;
    },
  ) => void;
  updateBacklogItem: (projectId: string, itemId: string, patch: Partial<BacklogItem>) => void;
  removeBacklogItem: (projectId: string, itemId: string) => void;
  /**
   * Переместить карточку между/внутри колонок: задать новый `status` и позицию `toIndex`.
   * Остальные карточки в обеих колонках пересчитываются.
   */
  moveBacklogItemToColumn: (
    projectId: string,
    itemId: string,
    toColumnId: string,
    toIndex: number,
  ) => void;
  /**
   * Перенести карточку канбана в другой проект (опционально — в конкретную
   * колонку целевого проекта). Делает запрос на сервер: тот атомарно
   * правит JSON, перемещает файлы вложений и создаёт ревизию.
   * После успеха перечитываем workspace, чтобы локальный стейт совпадал
   * с серверным (и не запускалась повторная PUT-сохранялка).
   */
  moveBacklogItemToProject: (
    fromProjectId: string,
    toProjectId: string,
    cardId: string,
    toColumnId?: string,
  ) => Promise<void>;

  addKanbanColumn: (projectId: string, label: string) => KanbanColumn | null;
  renameKanbanColumn: (projectId: string, columnId: string, label: string) => void;
  /**
   * Удалить колонку и перенести карточки в `fallbackColumnId`; если в колонке нет карточек —
   * `fallbackColumnId` можно не передавать. Нельзя удалить последнюю оставшуюся колонку.
   */
  removeKanbanColumn: (
    projectId: string,
    columnId: string,
    fallbackColumnId?: string,
  ) => { ok: true } | { ok: false; reason: 'last_column' | 'not_found' | 'no_fallback' };
  reorderKanbanColumns: (projectId: string, orderedIds: string[]) => void;

  updateGanttItem: (id: string, patch: Partial<GanttItem>) => void;
  addGanttItem: (item: Omit<GanttItem, 'id'>) => void;
  removeGanttItem: (id: string) => void;

  reviewSnapshots: ReviewSnapshot[];
  captureReviewSnapshot: (authorNote?: string) => Promise<void>;
  refreshReviewSnapshots: () => void;
}

const ProjectContext = createContext<ProjectContextValue | null>(null);

export function ProjectProvider({ children }: { children: React.ReactNode }) {
  const { user, canEdit, canSeeDocuments } = useAuth();
  const [projects, setProjects] = useState<EditableProject[]>([]);
  const [ganttItems, setGanttItems] = useState<GanttItem[]>([]);
  const [reviewSnapshots, setReviewSnapshots] = useState<ReviewSnapshot[]>([]);
  const [roadmapPhases, setRoadmapPhases] = useState<RoadmapPhase[]>([]);
  const [workspaceLoading, setWorkspaceLoading] = useState(true);

  const projectsRef = useRef(projects);
  projectsRef.current = projects;

  /** `updated_at` из SQLite workspace_documents — отправляем на PUT в `X-Workspace-Revised-At` (пустая строка ≙ null). */
  const projectsDocRevRef = useRef<string | null>(null);
  const ganttDocRevRef = useRef<string | null>(null);
  const roadmapDocRevRef = useRef<string | null>(null);

  function captureWorkspaceDocrevs(w: WorkspaceApiPayload) {
    const d = w.docRevisions;
    projectsDocRevRef.current = d?.projects ?? null;
    ganttDocRevRef.current = d?.ganttItems ?? null;
    roadmapDocRevRef.current = d?.roadmapPhases ?? null;
  }

  function ingestWorkspaceResponse(w: WorkspaceApiPayload) {
    const phases = normalizeRoadmapPhases(w.roadmapPhases);
    setRoadmapPhases(phases);
    const loadedProjects = (Array.isArray(w.projects) ? w.projects : []).map((p) =>
      normalizeEditableProject(p as EditableProject, phases),
    );
    const loadedGanttItems = (Array.isArray(w.ganttItems) ? w.ganttItems : []).map((g) =>
      normalizeGanttItem(g as GanttItem, phases),
    );
    const syncedGanttItems = syncGanttItemsStatusFromProjects(loadedGanttItems, loadedProjects);
    setProjects(loadedProjects);
    setGanttItems(syncedGanttItems);
    setReviewSnapshots(Array.isArray(w.reviewSnapshots) ? w.reviewSnapshots : []);
    captureWorkspaceDocrevs(w);
    // ВАЖНО: после любого ingest (load / SSE refresh / 409 reload) подавляем
    // ближайшие debounced auto-save для ВСЕХ трёх документов. Иначе цикл:
    // ingest → setGanttItems(synced) → useEffect gantt → PUT → broadcast →
    // SSE → ingest → … → stack overflow на десятках тысяч renders.
    skipNextProjectsSave.current = true;
    skipNextGanttSave.current = true;
    skipNextRoadmapSave.current = true;
  }

  /**
   * Безопасный вызов operation-API карточки/подзадачи. Optimistic UI уже
   * применён вызывающим кодом; если сервер вернул ошибку — refetch
   * workspace, чтобы локальное состояние совпало с актуальным на сервере
   * (правка пользователя при этом откатится, появится toast).
   */
  async function safeCardOp(label: string, op: () => Promise<{ revisedAt?: string } | unknown>): Promise<void> {
    try {
      const res = (await op()) as { revisedAt?: string };
      if (res && typeof res.revisedAt === 'string') {
        projectsDocRevRef.current = res.revisedAt;
      }
    } catch (err) {
      console.error(`diak: ${label} не удалось`, err);
      toast.error(`Не удалось сохранить (${label}). Подгружаю актуальные данные…`);
      try {
        const w = await apiJson<WorkspaceApiPayload>('/api/workspace');
        ingestWorkspaceResponse(w);
      } catch {
        toast.error('Не удалось подгрузить воркспейс. Обновите страницу (F5).');
      }
    }
  }

  async function reloadWorkspaceAfter409Conflict() {
    toast.error(
      'Воркспейс уже обновлён (другая вкладка или коллега). Подгружаем актуальные данные…',
    );
    try {
      const w = await apiJson<WorkspaceApiPayload>('/api/workspace');
      ingestWorkspaceResponse(w);
    } catch {
      toast.error('Не удалось подгрузить воркспейс. Обновите страницу (F5).');
    }
  }

  function headerRevisedAt(v: string | null): string {
    return v ?? '';
  }

  /**
   * Универсальный PUT с авто-merge при HTTP 409 «conflict_workspace_revision».
   * Если коллега опередил и сервер вернул конфликт — забираем актуальную
   * версию документа с сервера, мержим её с нашими локальными правками
   * (union по id), применяем merged-state в UI и пробуем PUT ещё раз. До
   * `maxRetries` повторов. После исчерпания — fallback на reloadWorkspace
   * (то же поведение что раньше). Возвращает revisedAt при успехе.
   */
  async function putWithMergeRetry<T>(opts: {
    url: string;
    payload: T;
    revRef: React.MutableRefObject<string | null>;
    skipNextRef: React.MutableRefObject<boolean>;
    applyLocal: (next: T) => void;
    pickServerState: (w: WorkspaceApiPayload) => T;
    pickServerRev: (w: WorkspaceApiPayload) => string | null;
    merge: (local: T, server: T) => T;
    label: string;
    maxRetries?: number;
  }): Promise<void> {
    const {
      url,
      revRef,
      skipNextRef,
      applyLocal,
      pickServerState,
      pickServerRev,
      merge,
      label,
      maxRetries = 3,
    } = opts;
    let payload = opts.payload;
    let mergedOnce = false;
    for (let attempt = 0; attempt <= maxRetries; attempt += 1) {
      try {
        const resp = await apiJson<{ ok?: true; revisedAt?: string }>(url, {
          method: 'PUT',
          headers: { 'X-Workspace-Revised-At': headerRevisedAt(revRef.current) },
          body: JSON.stringify(payload),
        });
        if (typeof resp?.revisedAt === 'string') revRef.current = resp.revisedAt;
        if (mergedOnce) {
          toast.success('Правки коллеги объединены с твоими — ничего не потеряно.');
        }
        return;
      } catch (err) {
        const stale =
          err instanceof ApiHttpError &&
          err.status === 409 &&
          (err.payload as { error?: string })?.error === 'conflict_workspace_revision';
        if (!stale || attempt === maxRetries) {
          if (stale) {
            await reloadWorkspaceAfter409Conflict();
          } else {
            console.error(`diak: сохранить ${label} не удалось`, err);
          }
          return;
        }
        try {
          const w = await apiJson<WorkspaceApiPayload>('/api/workspace');
          const serverState = pickServerState(w);
          const newRev = pickServerRev(w);
          if (newRev) revRef.current = newRev;
          payload = merge(payload, serverState);
          // Пропускаем следующий debounced-save, чтобы applyLocal не запустил
          // ещё один автоматический PUT через useEffect.
          skipNextRef.current = true;
          applyLocal(payload);
          mergedOnce = true;
        } catch (e) {
          console.error(`diak: merge ${label} не удалось`, e);
          await reloadWorkspaceAfter409Conflict();
          return;
        }
      }
    }
  }

  const skipNextProjectsSave = useRef(true);
  const skipNextGanttSave = useRef(true);
  const skipNextRoadmapSave = useRef(true);

  useEffect(() => {
    if (!user) {
      projectsDocRevRef.current = null;
      ganttDocRevRef.current = null;
      roadmapDocRevRef.current = null;
      setProjects([]);
      setGanttItems([]);
      setReviewSnapshots([]);
      setRoadmapPhases([]);
      setWorkspaceLoading(false);
      return;
    }
    let cancelled = false;
    setWorkspaceLoading(true);
    (async () => {
      try {
        const w = await apiJson<WorkspaceApiPayload>('/api/workspace');
        if (cancelled) return;
        ingestWorkspaceResponse(w);
      } catch {
        if (!cancelled) {
          setProjects([]);
          setGanttItems([]);
          setReviewSnapshots([]);
          setRoadmapPhases([]);
        }
      } finally {
        if (!cancelled) setWorkspaceLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user]);

  // ─── Real-time: SSE подписка + polling fallback ───────────────────────────
  // Сервер пушит уведомления через Server-Sent Events после каждой операции
  // (создание/правка карточки, перенос, удаление, изменение паспорта и т.д.).
  // Клиент при получении любого `*:changed` события подгружает актуальный
  // workspace и применяет его — UI обновляется в реальном времени.
  //
  // Polling раз в 30 секунд оставлен как fallback — если SSE-соединение
  // оборвётся надолго (nginx/прокси) и не восстановится сразу, polling
  // подхватит изменения. На горячем пути polling срабатывает редко, так
  // как revisedAt сравнивается с локальным и совпадение — норма.
  //
  // `lastLocalEditAtRef` — защита от перетирания: если пользователь только
  // что что-то правил, отложим refetch на 2.5 секунды.
  const lastLocalEditAtRef = useRef<number>(0);

  // Single-flight + дедупликация: realtime может прислать пачку событий
  // (собственное echo + правки коллеги). Чтобы не устроить setState-storm
  // (React #185 «Maximum update depth»), гарантируем:
  //   • не более одного активного refetch одновременно (inFlight);
  //   • не дёргаем, если только что сами правили (<2.5с);
  //   • после получения данных НИЧЕГО не пишем в state, если ревизия
  //     совпадает с локальной (это наше же echo) — выходим до setState.
  const refetchInFlightRef = useRef(false);
  // ВРЕМЕННО ОТКЛЮЧЕНО: realtime auto-refresh подозревается в setState-storm
  // (React #185 при перетаскивании карточек). Отключаем, чтобы изолировать
  // причину; диагностический componentStack в ErrorBoundary покажет точку,
  // если краш сохранится и БЕЗ realtime. Operation-API сохранения работают
  // как раньше; просто чужие правки не подтягиваются в реальном времени до
  // перезагрузки страницы (вернём, когда причина краша подтвердится).
  const REALTIME_REFETCH_ENABLED = false;
  const refetchWorkspace = useCallback(async () => {
    if (!REALTIME_REFETCH_ENABLED) return;
    if (refetchInFlightRef.current) return;
    if (Date.now() - lastLocalEditAtRef.current < 2500) return;
    refetchInFlightRef.current = true;
    try {
      const w = await apiJson<WorkspaceApiPayload>('/api/workspace');
      const serverRev = w.docRevisions?.projects ?? null;
      // Ключевая защита от цикла: если ревизия не изменилась относительно
      // того, что у нас уже есть — это наше собственное echo. НЕ трогаем
      // state вообще (иначе новые идентичности массивов → лишние ререндеры).
      if (!serverRev || serverRev === projectsDocRevRef.current) return;
      // Ещё раз проверяем «не правит ли пользователь прямо сейчас» — за время
      // сетевого round-trip он мог начать перетаскивать карточку.
      if (Date.now() - lastLocalEditAtRef.current < 2500) return;
      skipNextProjectsSave.current = true;
      skipNextGanttSave.current = true;
      skipNextRoadmapSave.current = true;
      ingestWorkspaceResponse(w);
    } catch {
      /* glush — повторим на следующем event/tick */
    } finally {
      refetchInFlightRef.current = false;
    }
  }, []);

  useEffect(() => {
    if (!user) return;
    if (typeof window === 'undefined' || typeof window.EventSource !== 'function') return;
    let cancelled = false;
    let es: EventSource | null = null;
    const open = () => {
      if (cancelled) return;
      es = new EventSource('/api/cards/stream', { withCredentials: true });
      es.onmessage = (ev) => {
        if (cancelled) return;
        try {
          const data = JSON.parse(ev.data) as { type?: string; revisedAt?: string };
          if (!data || typeof data.type !== 'string') return;
          if (data.type === 'hello') {
            // При первом сообщении сравним: если сервер ушёл вперёд за
            // время offline — подтянем сразу.
            if (data.revisedAt && data.revisedAt !== projectsDocRevRef.current) {
              void refetchWorkspace();
            }
            return;
          }
          // Любое *:changed → подтянуть актуальный workspace.
          if (data.type.endsWith(':changed')) {
            void refetchWorkspace();
          }
        } catch {
          /* ignore malformed events */
        }
      };
      es.onerror = () => {
        // Браузер сам переподключается, но на всякий случай форсим reopen
        // через 3 секунды если EventSource застрял в CLOSED.
        if (es && es.readyState === EventSource.CLOSED) {
          es.close();
          es = null;
          setTimeout(() => {
            if (!cancelled) open();
          }, 3000);
        }
      };
    };
    open();
    return () => {
      cancelled = true;
      es?.close();
      es = null;
    };
  }, [user, refetchWorkspace]);

  // Polling fallback: проверяем revisedAt раз в 30 секунд.
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    const tick = async () => {
      if (cancelled) return;
      if (typeof document !== 'undefined' && document.hidden) return;
      try {
        const rev = await apiJson<{ revisedAt: string | null }>('/api/cards/revision');
        if (cancelled) return;
        const serverRev = rev?.revisedAt ?? null;
        if (!serverRev) return;
        if (serverRev === projectsDocRevRef.current) return;
        await refetchWorkspace();
      } catch {
        /* ignore */
      }
    };
    const id = setInterval(() => {
      void tick();
    }, 30_000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [user, refetchWorkspace]);

  useEffect(() => {
    if (!user || !canEdit || workspaceLoading) return;
    if (skipNextProjectsSave.current) {
      skipNextProjectsSave.current = false;
      return;
    }
    lastLocalEditAtRef.current = Date.now();
    const t = setTimeout(() => {
      void apiJson<{ ok?: true; revisedAt?: string }>('/api/workspace/projects', {
        method: 'PUT',
        headers: {
          'X-Workspace-Revised-At': headerRevisedAt(projectsDocRevRef.current),
        },
        body: JSON.stringify(projects),
      })
        .then((resp) => {
          if (typeof resp?.revisedAt === 'string') projectsDocRevRef.current = resp.revisedAt;
          try {
            window.dispatchEvent(new CustomEvent('diak:projects-saved'));
          } catch {
            /* no-op */
          }
        })
        .catch(async (err) => {
          const stale =
            err instanceof ApiHttpError &&
            err.status === 409 &&
            (err.payload as { error?: string })?.error === 'conflict_workspace_revision';
          if (stale) {
            await reloadWorkspaceAfter409Conflict();
            return;
          }
          console.error('diak: сохранить projects не удалось', err);
        });
    }, 650);
    return () => clearTimeout(t);
  }, [projects, user, canEdit, workspaceLoading]);

  useEffect(() => {
    if (!user || !canEdit || workspaceLoading) return;
    if (skipNextGanttSave.current) {
      skipNextGanttSave.current = false;
      return;
    }
    const t = setTimeout(() => {
      void apiJson<{ ok?: true; revisedAt?: string }>('/api/workspace/gantt', {
        method: 'PUT',
        headers: {
          'X-Workspace-Revised-At': headerRevisedAt(ganttDocRevRef.current),
        },
        body: JSON.stringify(ganttItems),
      })
        .then((resp) => {
          if (typeof resp?.revisedAt === 'string') ganttDocRevRef.current = resp.revisedAt;
        })
        .catch(async (err) => {
          const stale =
            err instanceof ApiHttpError &&
            err.status === 409 &&
            (err.payload as { error?: string })?.error === 'conflict_workspace_revision';
          if (stale) {
            await reloadWorkspaceAfter409Conflict();
            return;
          }
          console.error('diak: сохранить gantt не удалось', err);
        });
    }, 650);
    return () => clearTimeout(t);
  }, [ganttItems, user, canEdit, workspaceLoading]);

  useEffect(() => {
    if (!user || !canEdit || workspaceLoading) return;
    if (skipNextRoadmapSave.current) {
      skipNextRoadmapSave.current = false;
      return;
    }
    const t = setTimeout(() => {
      void apiJson<{ ok?: true; revisedAt?: string }>('/api/workspace/roadmap-phases', {
        method: 'PUT',
        headers: {
          'X-Workspace-Revised-At': headerRevisedAt(roadmapDocRevRef.current),
        },
        body: JSON.stringify(roadmapPhases),
      })
        .then((resp) => {
          if (typeof resp?.revisedAt === 'string') roadmapDocRevRef.current = resp.revisedAt;
        })
        .catch(async (err) => {
          const stale =
            err instanceof ApiHttpError &&
            err.status === 409 &&
            (err.payload as { error?: string })?.error === 'conflict_workspace_revision';
          if (stale) {
            await reloadWorkspaceAfter409Conflict();
            return;
          }
          console.error('diak: сохранить roadmap-phases не удалось', err);
        });
    }, 650);
    return () => clearTimeout(t);
  }, [roadmapPhases, user, canEdit, workspaceLoading]);

  const mutateProject = useCallback((id: string, fn: (p: EditableProject) => EditableProject) => {
    if (!canEdit) return;
    setProjects((prev) => prev.map((p) => (p.id === id ? { ...fn(p), updatedAt: new Date().toISOString() } : p)));
  }, [canEdit]);

  const updateProject = useCallback(
    (id: string, patch: Partial<EditableProject>) => {
      if (!canEdit) return;
      const raw = patch as Record<string, unknown>;
      const { progress: _ignored, phase: _legacyPhase, ...rest } = raw;
      const phaseIdPatch = typeof rest.phaseId === 'string' ? rest.phaseId : undefined;

      setProjects((prev) => {
        const p = prev.find((x) => x.id === id);
        if (!p) return prev;
        const merged = { ...p, ...(rest as Partial<EditableProject>) };
        merged.ganttTasks = syncGanttTaskPlanStatusWithToday(merged.ganttTasks);
        merged.progress = computeProjectProgress(merged.ganttTasks);
        merged.updatedAt = new Date().toISOString();

        // Фиксируем дату фактической активации проекта при первом переходе в
        // статус "active". `activatedAt` храним один раз — это исторический
        // факт, не сбрасываем при последующих переключениях.
        if (
          merged.status === 'active' &&
          p.status !== 'active' &&
          !merged.activatedAt
        ) {
          const today = new Date();
          const yyyy = today.getFullYear();
          const mm = String(today.getMonth() + 1).padStart(2, '0');
          const dd = String(today.getDate()).padStart(2, '0');
          merged.activatedAt = `${yyyy}-${mm}-${dd}`;
        }

        if (phaseIdPatch !== undefined && phaseIdPatch !== p.phaseId) {
          const maxO = prev
            .filter((x) => x.id !== id && x.group === 'core' && x.phaseId === phaseIdPatch)
            .reduce((m, x) => Math.max(m, x.orderInPhase ?? -1), -1);
          merged.orderInPhase = maxO + 1;
          const meta = roadmapPhases.find((ph) => ph.id === phaseIdPatch);
          if (meta && typeof rest.phaseLabel !== 'string') {
            merged.phaseLabel = phaseLabelFromMeta(meta);
          }
        }

        return prev.map((x) => (x.id === id ? merged : x));
      });

      if (phaseIdPatch !== undefined) {
        setGanttItems((prev) => prev.map((g) => (g.projectId === id ? { ...g, phaseId: phaseIdPatch } : g)));
      }

      // Если статус проекта поменялся — синхронно обновляем все полосы общего
      // ганта, относящиеся к этому проекту, чтобы цвет колбаски следовал за
      // паспортом проекта.
      const statusPatch = (rest as { status?: unknown }).status;
      if (typeof statusPatch === 'string') {
        setGanttItems((prev) => {
          let changed = false;
          const next = prev.map((g) => {
            if (g.projectId !== id) return g;
            if (g.status === statusPatch) return g;
            changed = true;
            return { ...g, status: statusPatch as GanttItem['status'] };
          });
          return changed ? next : prev;
        });
      }
    },
    [canEdit, roadmapPhases],
  );

  const reorderCoreProjectsInPhase = useCallback((phaseId: string, orderedIds: string[]) => {
    if (!canEdit) return;
    const orderMap = new Map(orderedIds.map((pid, i) => [pid, i]));
    const now = new Date().toISOString();
    setProjects((prev) =>
      prev.map((p) => {
        if (p.group !== 'core' || p.phaseId !== phaseId) return p;
        const idx = orderMap.get(p.id);
        if (idx === undefined) return p;
        return { ...p, orderInPhase: idx, updatedAt: now };
      }),
    );
  }, [canEdit]);

  const reorderGanttItemsForPhase = useCallback((phaseId: string, orderedGanttIds: string[]) => {
    if (!canEdit) return;
    setGanttItems((prev) => {
      const phaseRows = prev.filter((g) => g.phaseId === phaseId);
      const byId = new Map(phaseRows.map((g) => [g.id, g]));
      if (orderedGanttIds.length !== phaseRows.length) return prev;
      if (!orderedGanttIds.every((id) => byId.has(id))) return prev;
      const newSequence = orderedGanttIds.map((id) => byId.get(id)!);
      const indices: number[] = [];
      prev.forEach((g, i) => {
        if (g.phaseId === phaseId) indices.push(i);
      });
      const next = [...prev];
      for (let k = 0; k < indices.length; k++) {
        next[indices[k]] = newSequence[k];
      }
      return next;
    });
  }, [canEdit]);

  const addRoadmapPhase = useCallback(() => {
    if (!canEdit) return;
    setRoadmapPhases((prev) => {
      const sorted = roadmapPhasesSorted(prev);
      const maxOrder = sorted.length ? Math.max(...sorted.map((x) => x.order)) : 0;
      const id = generateId('phase');
      return [
        ...sorted,
        {
          id,
          order: maxOrder + 1,
          label: `Этап ${sorted.length + 1}`,
          period: '',
          title: '',
          color: PHASE_COLORS_ROT[sorted.length % PHASE_COLORS_ROT.length],
        },
      ];
    });
  }, [canEdit]);

  const updateRoadmapPhase = useCallback((phaseId: string, patch: Partial<Omit<RoadmapPhase, 'id'>>) => {
    if (!canEdit) return;
    setRoadmapPhases((prev) => {
      const next = roadmapPhasesSorted(prev.map((p) => (p.id === phaseId ? { ...p, ...patch } : p)));
      const meta = next.find((p) => p.id === phaseId);
      if (meta) {
        setProjects((projects) =>
          projects.map((p) =>
            p.phaseId === phaseId ? { ...p, phaseLabel: phaseLabelFromMeta(meta) } : p,
          ),
        );
      }
      return next;
    });
  }, [canEdit]);

  const removeRoadmapPhase = useCallback(
    (phaseId: string) => {
      if (!canEdit) return;
      if (projectsRef.current.some((p) => p.group === 'core' && p.phaseId === phaseId)) {
        throw new Error(
          'Нельзя удалить этап: в нём есть профильные проекты. Перенесите или удалите их.',
        );
      }
      setRoadmapPhases((prev) => prev.filter((p) => p.id !== phaseId));
    },
    [canEdit],
  );

  const addCoreProject = useCallback(
    (phaseId: string) => {
      if (!canEdit) return;
      setProjects((prev) => {
        const maxO = prev
          .filter((x) => x.group === 'core' && x.phaseId === phaseId)
          .reduce((m, x) => Math.max(m, x.orderInPhase ?? -1), -1);
        const proj = emptyCoreProject(phaseId, roadmapPhases);
        proj.orderInPhase = maxO + 1;
        return [...prev, proj];
      });
    },
    [canEdit, roadmapPhases],
  );

  const removeProject = useCallback(
    (id: string) => {
      if (!canEdit) return;
      setProjects((prev) => prev.filter((p) => p.id !== id));
      setGanttItems((prev) => prev.filter((g) => g.projectId !== id));
    },
    [canEdit],
  );

  const addScopeItem = useCallback((pid: string, text: string) => {
    mutateProject(pid, (p) => ({ ...p, scope: [...p.scope, { text, done: false }] }));
  }, [mutateProject]);
  const updateScopeItem = useCallback((pid: string, idx: number, text: string) => {
    mutateProject(pid, (p) => {
      const s = [...p.scope];
      const cur = asScopeLine(s[idx]);
      s[idx] = { ...cur, text };
      return { ...p, scope: s };
    });
  }, [mutateProject]);
  const removeScopeItem = useCallback((pid: string, idx: number) => {
    mutateProject(pid, (p) => ({ ...p, scope: p.scope.filter((_, i) => i !== idx) }));
  }, [mutateProject]);

  const reorderScopeItems = useCallback((pid: string, oldIndex: number, newIndex: number) => {
    mutateProject(pid, (p) => {
      const s = [...p.scope];
      if (oldIndex < 0 || oldIndex >= s.length || newIndex < 0 || newIndex >= s.length) return p;
      const [removed] = s.splice(oldIndex, 1);
      s.splice(newIndex, 0, removed!);
      return { ...p, scope: s };
    });
  }, [mutateProject]);

  const toggleScopeItemDone = useCallback((pid: string, idx: number) => {
    mutateProject(pid, (p) => {
      const s = [...p.scope];
      if (idx < 0 || idx >= s.length) return p;
      const cur = asScopeLine(s[idx]);
      s[idx] = { ...cur, done: !cur.done };
      return { ...p, scope: s };
    });
  }, [mutateProject]);

  const addRisk = useCallback((pid: string, risk: Omit<AgileRisk, 'id' | 'createdAt' | 'updatedAt'>) => {
    const now = new Date().toISOString();
    mutateProject(pid, (p) => ({
      ...p,
      risks: [...p.risks, { ...risk, id: generateId('risk'), createdAt: now, updatedAt: now }],
    }));
  }, [mutateProject]);
  const updateRisk = useCallback((pid: string, riskId: string, patch: Partial<AgileRisk>) => {
    mutateProject(pid, (p) => ({
      ...p,
      risks: p.risks.map((r) =>
        r.id === riskId ? { ...r, ...patch, updatedAt: new Date().toISOString() } : r,
      ),
    }));
  }, [mutateProject]);
  const removeRisk = useCallback((pid: string, riskId: string) => {
    mutateProject(pid, (p) => ({ ...p, risks: p.risks.filter((r) => r.id !== riskId) }));
  }, [mutateProject]);

  const addKPI = useCallback((pid: string, kpi: Omit<KPI, 'id'>) => {
    mutateProject(pid, (p) => ({ ...p, kpis: [...p.kpis, { ...kpi, id: generateId('kpi') }] }));
  }, [mutateProject]);
  const updateKPI = useCallback((pid: string, kpiId: string, patch: Partial<KPI>) => {
    mutateProject(pid, (p) => ({
      ...p,
      kpis: p.kpis.map((k) => (k.id === kpiId ? { ...k, ...patch } : k)),
    }));
  }, [mutateProject]);
  const removeKPI = useCallback((pid: string, kpiId: string) => {
    mutateProject(pid, (p) => ({ ...p, kpis: p.kpis.filter((k) => k.id !== kpiId) }));
  }, [mutateProject]);

  const addStakeholder = useCallback((pid: string, sh: Omit<Stakeholder, 'id'>) => {
    mutateProject(pid, (p) => ({ ...p, stakeholders: [...p.stakeholders, { ...sh, id: generateId('sh') }] }));
  }, [mutateProject]);
  const updateStakeholder = useCallback((pid: string, shId: string, patch: Partial<Stakeholder>) => {
    mutateProject(pid, (p) => ({
      ...p,
      stakeholders: p.stakeholders.map((s) => (s.id === shId ? { ...s, ...patch } : s)),
    }));
  }, [mutateProject]);
  const removeStakeholder = useCallback((pid: string, shId: string) => {
    mutateProject(pid, (p) => ({ ...p, stakeholders: p.stakeholders.filter((s) => s.id !== shId) }));
  }, [mutateProject]);

  const addGanttTask = useCallback((pid: string, task: Omit<EditableGanttTask, 'id'>) => {
    mutateProject(pid, (p) => {
      const ganttTasks = syncGanttTaskPlanStatusWithToday([...p.ganttTasks, { ...task, id: generateId('gt') }]);
      return { ...p, ganttTasks, progress: computeProjectProgress(ganttTasks) };
    });
  }, [mutateProject]);
  const updateGanttTask = useCallback((pid: string, taskId: string, patch: Partial<EditableGanttTask>) => {
    mutateProject(pid, (p) => {
      const ganttTasks = syncGanttTaskPlanStatusWithToday(
        p.ganttTasks.map((t) => (t.id === taskId ? { ...t, ...patch } : t)),
      );
      return { ...p, ganttTasks, progress: computeProjectProgress(ganttTasks) };
    });
  }, [mutateProject]);
  const removeGanttTask = useCallback((pid: string, taskId: string) => {
    mutateProject(pid, (p) => {
      const ganttTasks = syncGanttTaskPlanStatusWithToday(p.ganttTasks.filter((t) => t.id !== taskId));
      const documents = p.documents.map((d) =>
        d.sourceGanttTaskId === taskId ? { ...d, sourceGanttTaskId: undefined } : d,
      );
      return {
        ...p,
        documents,
        ganttTasks,
        progress: computeProjectProgress(ganttTasks),
      };
    });
  }, [mutateProject]);

  const reorderGanttTasksAfterDrag = useCallback(
    (pid: string, activeTaskId: string, overTaskId: string) => {
      if (activeTaskId === overTaskId) return;
      mutateProject(pid, (p) => {
        const sorted = [...p.ganttTasks].sort(compareGanttTasksDisplayOrder);
        const oldIdx = sorted.findIndex((t) => t.id === activeTaskId);
        const newIdx = sorted.findIndex((t) => t.id === overTaskId);
        if (oldIdx < 0 || newIdx < 0) return p;
        const nextOrder = [...sorted];
        const [moved] = nextOrder.splice(oldIdx, 1);
        nextOrder.splice(newIdx, 0, moved);
        const byId = new Map(nextOrder.map((t, i) => [t.id, { ...t, planOrder: i }]));
        const ganttTasks = syncGanttTaskPlanStatusWithToday(p.ganttTasks.map((t) => byId.get(t.id) ?? t));
        return {
          ...p,
          ganttTasks,
          progress: computeProjectProgress(ganttTasks),
        };
      });
    },
    [mutateProject],
  );

  const addBacklogItem = useCallback(
    (
      pid: string,
      item: Omit<BacklogItem, 'id' | 'createdAt' | 'updatedAt' | 'kanbanOrder'> & {
        kanbanOrder?: number;
      },
    ) => {
      const now = new Date().toISOString();
      const cardId = generateId('bl');
      let createdCard: BacklogItem | null = null;
      mutateProject(pid, (p) => {
        const sameCol = p.backlog.filter((b) => b.status === item.status);
        const kanbanOrder =
          typeof item.kanbanOrder === 'number' && Number.isFinite(item.kanbanOrder)
            ? item.kanbanOrder
            : sameCol.length;
        const next: BacklogItem = {
          ...(item as Omit<BacklogItem, 'id' | 'createdAt' | 'updatedAt'>),
          kanbanOrder,
          id: cardId,
          createdAt: now,
          updatedAt: now,
        };
        createdCard = next;
        return { ...p, backlog: [...p.backlog, next] };
      });
      if (createdCard) {
        void safeCardOp('создание карточки', () =>
          apiCreateCard(pid, createdCard as unknown as { id: string } & Record<string, unknown>),
        );
      }
    },
    [mutateProject],
  );
  const updateBacklogItem = useCallback(
    (pid: string, itemId: string, patch: Partial<BacklogItem>) => {
      let serverPatch: Record<string, unknown> | null = null;
      mutateProject(pid, (p) => {
        const curr = p.backlog.find((b) => b.id === itemId);
        if (!curr) return p;
        // Если меняется колонка (status) через обычный updateBacklogItem — ставим карточку в конец новой.
        const columnChanged =
          typeof patch.status === 'string' && patch.status !== curr.status;
        if (!columnChanged) {
          serverPatch = { ...(patch as Record<string, unknown>) };
          return {
            ...p,
            backlog: p.backlog.map((b) =>
              b.id === itemId ? { ...b, ...patch, updatedAt: new Date().toISOString() } : b,
            ),
          };
        }
        const toCol = patch.status as string;
        const now = new Date().toISOString();
        const maxOrder = p.backlog
          .filter((b) => b.status === toCol && b.id !== itemId)
          .reduce((m, b) => Math.max(m, b.kanbanOrder), -1);
        const next = p.backlog.map((b) =>
          b.id === itemId
            ? {
                ...b,
                ...patch,
                status: toCol,
                kanbanOrder: maxOrder + 1,
                updatedAt: now,
              }
            : b,
        );
        // Переупаковать исходную колонку, чтобы не осталось дыр.
        const fromCol = curr.status;
        const repackedFrom = next
          .filter((b) => b.status === fromCol)
          .sort((a, b) => a.kanbanOrder - b.kanbanOrder)
          .map((b, i) => ({ ...b, kanbanOrder: i }));
        const byId = new Map(repackedFrom.map((b) => [b.id, b]));
        serverPatch = {
          ...(patch as Record<string, unknown>),
          status: toCol,
          kanbanOrder: maxOrder + 1,
        };
        return {
          ...p,
          backlog: next.map((b) => byId.get(b.id) ?? b),
        };
      });
      if (serverPatch) {
        void safeCardOp('правка карточки', () => apiPatchCard(pid, itemId, serverPatch!));
      }
    },
    [mutateProject],
  );
  const removeBacklogItem = useCallback((pid: string, itemId: string) => {
    let existed = false;
    mutateProject(pid, (p) => {
      const target = p.backlog.find((b) => b.id === itemId);
      if (!target) return p;
      existed = true;
      const col = target.status;
      const rest = p.backlog.filter((b) => b.id !== itemId);
      // Переупаковать колонку после удаления.
      const repacked = rest
        .filter((b) => b.status === col)
        .sort((a, b) => a.kanbanOrder - b.kanbanOrder)
        .map((b, i) => ({ ...b, kanbanOrder: i }));
      const byId = new Map(repacked.map((b) => [b.id, b]));
      return { ...p, backlog: rest.map((b) => byId.get(b.id) ?? b) };
    });
    if (existed) {
      void safeCardOp('удаление карточки', () => apiDeleteCard(pid, itemId));
    }
  }, [mutateProject]);

  const moveBacklogItemToColumn = useCallback(
    (pid: string, itemId: string, toColumnId: string, toIndex: number) => {
      let movedStatus: string | null = null;
      let movedOrder: number | null = null;
      mutateProject(pid, (p) => {
        const target = p.backlog.find((b) => b.id === itemId);
        if (!target) return p;
        if (!p.kanbanColumns.some((c) => c.id === toColumnId)) return p;
        const fromCol = target.status;
        const now = new Date().toISOString();
        // Собрать списки «без перемещаемой» отдельно для каждой затронутой колонки.
        const othersIn = (colId: string) =>
          p.backlog
            .filter((b) => b.status === colId && b.id !== itemId)
            .sort((a, b) => a.kanbanOrder - b.kanbanOrder);
        const toList = othersIn(toColumnId);
        const clampedIdx = Math.max(0, Math.min(toIndex, toList.length));
        const newTarget: BacklogItem = {
          ...target,
          status: toColumnId,
          kanbanOrder: clampedIdx,
          updatedAt: now,
        };
        const rebuiltTo = [...toList.slice(0, clampedIdx), newTarget, ...toList.slice(clampedIdx)].map(
          (b, i) => ({ ...b, kanbanOrder: i }),
        );
        let updated: BacklogItem[];
        if (fromCol === toColumnId) {
          const byId = new Map(rebuiltTo.map((b) => [b.id, b]));
          updated = p.backlog.map((b) => byId.get(b.id) ?? b);
        } else {
          const rebuiltFrom = othersIn(fromCol).map((b, i) => ({ ...b, kanbanOrder: i }));
          const byId = new Map<string, BacklogItem>();
          for (const b of rebuiltFrom) byId.set(b.id, b);
          for (const b of rebuiltTo) byId.set(b.id, b);
          updated = p.backlog.map((b) => byId.get(b.id) ?? b);
        }
        movedStatus = toColumnId;
        movedOrder = clampedIdx;
        return { ...p, backlog: updated };
      });
      if (movedStatus !== null && movedOrder !== null) {
        void safeCardOp('перенос карточки', () =>
          apiMoveCard(pid, itemId, { status: movedStatus!, kanbanOrder: movedOrder! }),
        );
      }
    },
    [mutateProject],
  );

  const moveBacklogItemToProject = useCallback(
    async (
      fromProjectId: string,
      toProjectId: string,
      cardId: string,
      toColumnId?: string,
    ) => {
      if (!canEdit) return;
      await moveBacklogCard({ fromProjectId, toProjectId, cardId, toColumnId });
      // На сервере уже обновлены `projects` (с ревизией) и перенесены вложения.
      // Перечитываем workspace, чтобы локальный стейт совпал, и подавляем
      // ближайшую авто-сохранялку, чтобы не отправить только что полученный
      // массив снова.
      const w = await apiJson<WorkspaceApiPayload>('/api/workspace');
      ingestWorkspaceResponse(w);
    },
    [canEdit],
  );

  const addKanbanColumn = useCallback(
    (pid: string, label: string): KanbanColumn | null => {
      if (!canEdit) return null;
      const name = label.trim() || 'Новая колонка';
      let created: KanbanColumn | null = null;
      setProjects((prev) =>
        prev.map((p) => {
          if (p.id !== pid) return p;
          // Сгенерировать уникальный в рамках проекта id (slug от названия → fallback к generateId).
          const existing = new Set(p.kanbanColumns.map((c) => c.id));
          const slug = name
            .toLowerCase()
            .replace(/[^a-z0-9а-яё]+/gi, '-')
            .replace(/(^-|-$)/g, '')
            .slice(0, 40);
          let id = slug || generateId('col');
          while (existing.has(id)) id = `${slug || 'col'}-${Math.random().toString(36).slice(2, 6)}`;
          const nextOrder = p.kanbanColumns.length;
          const col: KanbanColumn = { id, label: name, order: nextOrder };
          created = col;
          return {
            ...p,
            kanbanColumns: [...p.kanbanColumns, col],
            updatedAt: new Date().toISOString(),
          };
        }),
      );
      return created;
    },
    [canEdit],
  );

  const renameKanbanColumn = useCallback(
    (pid: string, columnId: string, label: string) => {
      if (!canEdit) return;
      const name = label.trim();
      if (!name) return;
      mutateProject(pid, (p) => ({
        ...p,
        kanbanColumns: p.kanbanColumns.map((c) =>
          c.id === columnId ? { ...c, label: name } : c,
        ),
      }));
    },
    [canEdit, mutateProject],
  );

  const removeKanbanColumn = useCallback<
    ProjectContextValue['removeKanbanColumn']
  >(
    (pid, columnId, fallbackColumnId) => {
      if (!canEdit) return { ok: false, reason: 'not_found' };
      const project = projectsRef.current.find((p) => p.id === pid);
      if (!project) return { ok: false, reason: 'not_found' };
      const target = project.kanbanColumns.find((c) => c.id === columnId);
      if (!target) return { ok: false, reason: 'not_found' };
      if (project.kanbanColumns.length <= 1) return { ok: false, reason: 'last_column' };
      const hasCards = project.backlog.some((b) => b.status === columnId);
      const fallback =
        fallbackColumnId && fallbackColumnId !== columnId
          ? project.kanbanColumns.find((c) => c.id === fallbackColumnId)
          : undefined;
      if (hasCards && !fallback) return { ok: false, reason: 'no_fallback' };

      setProjects((prev) =>
        prev.map((p) => {
          if (p.id !== pid) return p;
          const remainingCols = p.kanbanColumns
            .filter((c) => c.id !== columnId)
            .sort((a, b) => a.order - b.order)
            .map((c, i) => ({ ...c, order: i }));
          if (!hasCards) {
            return {
              ...p,
              kanbanColumns: remainingCols,
              updatedAt: new Date().toISOString(),
            };
          }
          const fallbackId = fallback!.id;
          const now = new Date().toISOString();
          // Карточки переносимой колонки идут в хвост fallback, сохраняя относительный порядок.
          const movedOrdered = p.backlog
            .filter((b) => b.status === columnId)
            .sort((a, b) => a.kanbanOrder - b.kanbanOrder);
          const fallbackExisting = p.backlog
            .filter((b) => b.status === fallbackId)
            .sort((a, b) => a.kanbanOrder - b.kanbanOrder);
          const rebuiltFallback = [
            ...fallbackExisting,
            ...movedOrdered.map((b) => ({ ...b, status: fallbackId, updatedAt: now })),
          ].map((b, i) => ({ ...b, kanbanOrder: i }));
          const byId = new Map(rebuiltFallback.map((b) => [b.id, b]));
          const nextBacklog = p.backlog.map((b) => byId.get(b.id) ?? b);
          return {
            ...p,
            kanbanColumns: remainingCols,
            backlog: nextBacklog,
            updatedAt: now,
          };
        }),
      );
      return { ok: true };
    },
    [canEdit],
  );

  const reorderKanbanColumns = useCallback(
    (pid: string, orderedIds: string[]) => {
      if (!canEdit) return;
      mutateProject(pid, (p) => {
        const byId = new Map(p.kanbanColumns.map((c) => [c.id, c]));
        const rebuilt: KanbanColumn[] = [];
        orderedIds.forEach((id, i) => {
          const c = byId.get(id);
          if (c) rebuilt.push({ ...c, order: i });
        });
        // Добавить колонки, не попавшие в orderedIds, в хвост.
        for (const c of p.kanbanColumns) {
          if (!orderedIds.includes(c.id)) rebuilt.push({ ...c, order: rebuilt.length });
        }
        return { ...p, kanbanColumns: rebuilt };
      });
    },
    [canEdit, mutateProject],
  );

  const updateGanttItem = useCallback(
    (id: string, patch: Partial<GanttItem>) => {
      if (!canEdit) return;
      setGanttItems((prev) => prev.map((g) => (g.id === id ? { ...g, ...patch } : g)));
    },
    [canEdit],
  );
  const addGanttItem = useCallback(
    (item: Omit<GanttItem, 'id'>) => {
      if (!canEdit) return;
      setGanttItems((prev) => [...prev, { ...item, id: generateId('g') }]);
    },
    [canEdit],
  );
  const removeGanttItem = useCallback(
    (id: string) => {
      if (!canEdit) return;
      setGanttItems((prev) => prev.filter((g) => g.id !== id));
    },
    [canEdit],
  );

  const refreshReviewSnapshots = useCallback(() => {
    if (!user) return;
    apiJson<{ reviewSnapshots: ReviewSnapshot[] }>('/api/workspace')
      .then((w) => setReviewSnapshots(Array.isArray(w.reviewSnapshots) ? w.reviewSnapshots : []))
      .catch(() => {});
  }, [user]);

  const captureReviewSnapshot = useCallback(
    async (authorNote?: string) => {
      if (!canEdit || !user) return;
      const last = reviewSnapshots[reviewSnapshots.length - 1];
      const snap = await buildReviewSnapshotRecord(
        projects,
        ganttItems,
        authorNote ?? '',
        last?.contentHash ?? null,
      );
      await apiJson('/api/workspace/review-snapshots', {
        method: 'POST',
        body: JSON.stringify(snap),
      });
      refreshReviewSnapshots();
    },
    [projects, ganttItems, reviewSnapshots, canEdit, user, refreshReviewSnapshots],
  );

  const ganttTasksFingerprint = useMemo(
    () =>
      projects
        .map((p) => `${p.id}:${p.ganttTasks.map((t) => `${t.id}:${t.progress}:${t.status}`).join(';')}`)
        .join('|'),
    [projects],
  );

  const syncProjectProgress = useCallback(() => {
    setProjects((prev) => {
      let changed = false;
      const next = prev.map((p) => {
        const ganttTasks = syncGanttTaskPlanStatusWithToday(p.ganttTasks);
        const progress = computeProjectProgress(ganttTasks);
        const statusTouched = p.ganttTasks.some((t, i) => ganttTasks[i] && t.status !== ganttTasks[i]!.status);
        if (!statusTouched && progress === p.progress) return p;
        changed = true;
        return { ...p, ganttTasks, progress };
      });
      return changed ? next : prev;
    });
  }, []);

  useEffect(() => {
    syncProjectProgress();
  }, [ganttTasksFingerprint, syncProjectProgress]);

  useEffect(() => {
    const onVis = () => {
      if (document.visibilityState === 'visible') syncProjectProgress();
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [syncProjectProgress]);

  return (
    <ProjectContext.Provider
      value={{
        projects,
        ganttItems,
        reviewSnapshots,
        roadmapPhases,
        workspaceLoading,
        canEdit,
        canSeeDocuments,
        captureReviewSnapshot,
        refreshReviewSnapshots,
        updateProject,
        reorderCoreProjectsInPhase,
        reorderGanttItemsForPhase,
        addRoadmapPhase,
        updateRoadmapPhase,
        removeRoadmapPhase,
        addCoreProject,
        removeProject,
        addScopeItem,
        updateScopeItem,
        removeScopeItem,
        reorderScopeItems,
        toggleScopeItemDone,
        addRisk,
        updateRisk,
        removeRisk,
        addKPI,
        updateKPI,
        removeKPI,
        addStakeholder,
        updateStakeholder,
        removeStakeholder,
        addGanttTask,
        updateGanttTask,
        removeGanttTask,
        reorderGanttTasksAfterDrag,
        addBacklogItem,
        updateBacklogItem,
        removeBacklogItem,
        moveBacklogItemToColumn,
        moveBacklogItemToProject,
        addKanbanColumn,
        renameKanbanColumn,
        removeKanbanColumn,
        reorderKanbanColumns,
        updateGanttItem,
        addGanttItem,
        removeGanttItem,
      }}
    >
      {children}
    </ProjectContext.Provider>
  );
}

export function useProjects() {
  const ctx = useContext(ProjectContext);
  if (!ctx) throw new Error('useProjects must be used inside ProjectProvider');
  return ctx;
}
